import {recordRumAction} from '../../util/datadog/datadogRum';
import {makeTrackEvent} from './makeTrackEvent';

type DiscoverType = 'registries' | 'collections' | 'versions';

type RegistryAction =
  | 'registry_detail_tab_changed'
  | 'landing_page_tab_changed'
  | 'discover_type_changed'
  | 'discover_filter_changed'
  | 'discover_search_applied';

export type TrackRegistriesEvent = {
  action: RegistryAction;
  organizationName: string;
  discoverType?: DiscoverType;
  previousType?: DiscoverType;
  organizationID?: string;
  entityName?: string;
  newTab?: string;
  previousTab?: string;
  registryName?: string;
  activeFilters?: string[];
  filterField?: string;
  filterOperator?: string;
};

const trackRegistriesInteraction = makeTrackEvent<
  TrackRegistriesEvent,
  {
    action: {
      description: 'The registries interaction action.';
      exampleValues: [
        'registry_detail_tab_changed',
        'landing_page_tab_changed',
        'discover_type_changed',
        'discover_filter_changed',
        'discover_search_applied',
      ];
    };
    discoverType: {
      description: 'The newly selected discover type in registries discover.';
      exampleValues: ['registries', 'collections', 'versions'];
    };
    previousType: {
      description: 'The previously selected discover type in registries discover.';
      exampleValues: ['registries', 'collections', 'versions'];
    };
    organizationID: {
      description: 'Organization identifier for the registries interaction.';
      exampleValues: ['<hash>'];
    };
    organizationName: {
      description: 'Organization name for the registries interaction.';
      exampleValues: ['<name>'];
    };
    entityName: {
      description: 'Registry entity name associated with a tab interaction.';
      exampleValues: ['<name>'];
    };
    newTab: {
      description: 'The new tab selected in the Registry Details Page.';
      exampleValues: ['collections', 'automations', 'overview', 'discover'];
    };
    previousTab: {
      description: 'The previous tab selected in the Registry Details Page.';
      exampleValues: ['collections', 'automations', 'overview', 'discover'];
    };
    registryName: {
      description: 'The registry name where the tab interaction occurred.';
      exampleValues: ['model', 'dataset'];
    };
    activeFilters: {
      description: 'Snapshot of all active filter fields at the time of filter change on the Registries Discover Page.';
      exampleValues: [['type'], ['type', 'tag', 'registry']];
    };
    filterField: {
      description: 'The filter field that changed on the Registries Discover Page.';
      exampleValues: ['name', 'created_at', 'registry_name', 'collection_tag'];
    };
    filterOperator: {
      description: 'The operator on the changed filter on the Registries Discover Page.';
      exampleValues: ['contains', 'equals', 'after', 'before'];
    };
    _description: 'Tracks user interactions with the Registry Details Page, Registries Landing Page and Discover Tab.';
    _location: 'registries pages';
    _motivation: 'Understand how users navigate registries surfaces and tabs over time.';
  }
>('Registries Interaction');

export function trackRegistriesEvent(eventData: TrackRegistriesEvent): void {
  trackRegistriesInteraction(eventData);
  recordRumAction(`Registries.${eventData.action}`, eventData);
}
